import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { PublicActions } from './public.actions';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { EStorageKey } from 'src/app/app.enums';
import { parseError } from 'ngx-mt-ui-lib/functions';
import { PublicApiService } from '@app/core/api/public-api.service';

@Injectable()
export class PublicEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly publicApiService = inject(PublicApiService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  readonly initStart$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicActions.initStart),
        tap(() => {
          const publicSystemParams = localStorage.getItemJson<
            Record<string, string>
          >(EStorageKey.PUBLIC_SYSTEM_PARAMS);

          if (!publicSystemParams) {
            this.store.dispatch(PublicActions.requestPublicSystemParams());
          }
          this.store.dispatch(
            PublicActions.initEnd({
              publicSystemParams,
            }),
          );
        }),
      ),
    { dispatch: false },
  );

  readonly requestPublicSystemParams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PublicActions.requestPublicSystemParams),
      switchMap(() =>
        this.publicApiService.getPublicParameters().pipe(
          map((res) =>
            PublicActions.requestPublicSystemParamsSuccess({
              publicSystemParams: Object.fromEntries(
                res.systemParameters.map((p) => [p.name, p.value]),
              ),
            }),
          ),
          catchError((error) =>
            of(PublicActions.requestPublicSystemParamsError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestPublicSystemParamsSuccess = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicActions.requestPublicSystemParamsSuccess),
        tap((action) => {
          localStorage.setItemJson(
            EStorageKey.PUBLIC_SYSTEM_PARAMS,
            action.publicSystemParams,
          );
        }),
      ),
    { dispatch: false },
  );

  readonly showError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicActions.requestPublicSystemParamsError),
        tap((action) => {
          this.toastService.add({
            level: 'error',
            summary: this.translateService.instant('GENERAL.ERROR_REQUEST'),
            detail: parseError(action.error),
          });
        }),
      ),
    { dispatch: false },
  );
}
